import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectCurrentProject,
  selectHeaderXpData,
  selectXpData,
} from '../modules/missionControl/selectors'
import {
  setHeaderXPData,
  setTaskSubmissions,
  setXPData,
  setXpRecords,
} from '../modules/missionControl/slice'
import { theseus } from '../api/theseus'
import { formatSubmissions } from './useMissionControlPoolStateManagement'
import useUserSlicedLeaderboard from '../page_components/DropDetails/useUserSlicedLeaderboard'
import { selectUser } from '../modules/user/selectors'

const useFetchAndUpdateDropTaskSubmissions = () => {
  const project = useSelector(selectCurrentProject)
  const projectId = project?.id

  const dispatch = useDispatch()
  const { handleFetchUserSlicedLeaderboard } = useUserSlicedLeaderboard()

  const fetchAndUpdateTaskSubmissions = useCallback(async () => {
    return new Promise(async (resolve, reject) => {
      try {
        if (!projectId) return
        const { taskSubmissions, xpRecords, xpData } =
          await fetchUserProjectDropData(projectId)
        if (!taskSubmissions) throw new Error('No task submissions')

        handleFetchUserSlicedLeaderboard({ projectId })

        dispatch(setTaskSubmissions(taskSubmissions))
        dispatch(setXpRecords(xpRecords))
        dispatch(setXPData(xpData))
        resolve({ taskSubmissions, xpData })
      } catch (error) {
        reject(error)
      }
    })
  }, [projectId, handleFetchUserSlicedLeaderboard, dispatch])

  return fetchAndUpdateTaskSubmissions
}

export default useFetchAndUpdateDropTaskSubmissions

async function fetchUserProjectDropData(projectId: number) {
  try {
    const promises = [theseus.get(`user/submissions/${projectId}/drop`)]

    const [{ data }] = await Promise.all(promises)

    if (!data) throw new Error('No user submissions')

    const {
      submissions: unformattedTaskSubmissionsResponse,
      xpRecords,
      xpData,
    } = data

    const unformattedTaskSubmissions = unformattedTaskSubmissionsResponse

    const taskSubmissions = formatSubmissions(unformattedTaskSubmissions)

    return {
      taskSubmissions,
      xpRecords,
      xpData,
      hasProject: true,
      accountDetails: {},
      isBlocked: false,
      projects: [],
      competitions: [],
      wonPrizes: [],
    }
  } catch (error) {
    console.error(error)
    return {
      taskSubmissions: {},
      xpRecords: [],
      totalAllocation: 0,
      xpData: {},
      headerXpData: {},
      hasProject: false,
      accountDetails: {},
      isBlocked: false,
      projects: [],
      wonPrizes: [],
    }
  }
}

export function useZapDropXpData() {
  const dispatch = useDispatch()
  const xpData = useSelector(selectHeaderXpData)

  const fetchAndUpdateZapDropXpData = useCallback(async () => {
    return new Promise(async (resolve, reject) => {
      try {
        const { xpData } = await fetchUserZapDropXp()
        dispatch(setHeaderXPData(xpData))
        resolve(xpData)
      } catch (error) {
        reject(error)
      }
    })
  }, [dispatch])

  return { fetchAndUpdateZapDropXpData, xpData }
}

async function fetchUserZapDropXp() {
  try {
    const { data } = await theseus.get(`user/xp/zap-drops`)

    if (!data) throw new Error('No user xp data')

    return {
      xpData: data?.xpData,
    }
  } catch (error) {
    console.error(error)
    return {
      xpData: {},
    }
  }
}
